import { Suspense, useState, useEffect, type ReactNode } from 'react';
import { BrowserRouter, useRoutes } from 'react-router-dom';
import { DataProvider } from '@/data/data-provider';
import { createAppRoutes } from '@/app/routes';
import { useRepository } from '@/data/data-provider';
import { seedDevData } from '@/data/seed-dev';
import CookieConsent from '@/shared/components/cookie-consent/CookieConsent';
import { ToastProvider } from '@/shared/components';
import { initGA } from '@/services/analytics';
import { useRouteTracking } from '@/shared/hooks/useRouteTracking';
import '@/config/i18n'; // side-effect: initializes i18next
import '@/app/App.css';

// Initialise GA synchronously before React renders if cookies were previously accepted.
// This ensures the first page_view tracked by useRouteTracking isn't dropped.
try {
  if (localStorage.getItem('ezshift-cookies-accepted') === 'true') {
    initGA();
  }
} catch {
  // Storage unavailable — skip
}

/**
 * Full-page skeleton shown while i18n + fonts load and the app hydrates.
 * Mimics the real AppShell layout so the transition is seamless.
 */
function AppLoadingFallback() {
  return (
    <div className="app-skeleton">
      {/* Sidebar skeleton */}
      <div className="app-skeleton__sidebar">
        <div className="app-skeleton__sidebar-logo" />
        <div className="app-skeleton__sidebar-item" />
        <div className="app-skeleton__sidebar-item" />
        <div className="app-skeleton__sidebar-item" />
        <div className="app-skeleton__sidebar-item" />
        <div className="app-skeleton__sidebar-item" />
      </div>

      {/* Topbar skeleton */}
      <div className="app-skeleton__topbar">
        <div className="app-skeleton__topbar-title" />
        <div className="app-skeleton__topbar-spacer" />
        <div className="app-skeleton__topbar-avatar" />
      </div>

      {/* Content area skeleton */}
      <div className="app-skeleton__content">
        <div className="app-skeleton__content-header" />
        <div className="app-skeleton__content-stats">
          <div className="app-skeleton__content-stat" />
          <div className="app-skeleton__content-stat" />
          <div className="app-skeleton__content-stat" />
          <div className="app-skeleton__content-stat" />
        </div>
        <div className="app-skeleton__content-body" />
      </div>

      {/* Mobile bottom bar skeleton */}
      <div className="app-skeleton__bottom-bar">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="app-skeleton__bottom-tab">
            <div className="app-skeleton__bottom-icon" />
            <div className="app-skeleton__bottom-label" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Gates children behind font readiness.
 * Explicitly triggers Material Symbols font load and polls until available,
 * then renders children. Falls back after timeout.
 */
function FontGate({ children, fallback }: { children: ReactNode; fallback: ReactNode }) {
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function waitForFonts() {
      try {
        // Explicitly trigger loading of the icon font
        await document.fonts.load('24px "Material Symbols Rounded"');

        // Poll until the font is actually usable (handles race with CSS parsing)
        let attempts = 0;
        while (
          !document.fonts.check('24px "Material Symbols Rounded"') &&
          attempts < 50
        ) {
          await new Promise(r => setTimeout(r, 50));
          attempts++;
        }
      } catch {
        // Font API not supported or font failed — proceed anyway
      }
      if (!cancelled) setFontsReady(true);
    }

    void waitForFonts();

    // Safety timeout — don't block forever
    const timer = setTimeout(() => {
      if (!cancelled) setFontsReady(true);
    }, 4000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  if (!fontsReady) return <>{fallback}</>;
  return <>{children}</>;
}

/**
 * Resolves the route tree via `useRoutes`.
 * Must be rendered inside a `<BrowserRouter>`.
 */
function AppRoutes() {
  useRouteTracking();
  const routes = useRoutes(createAppRoutes());
  return routes;
}

/**
 * Root application component.
 *
 * Wraps the app with:
 *   1. BrowserRouter — client-side routing
 *   2. DataProvider — repository context (LocalRepository in Phase 1)
 *   3. Suspense — loading fallback for i18n and lazy routes
 *   4. FontGate — waits for Material Symbols + text fonts to load
 *   5. CookieConsent — GDPR cookie consent banner (global)
 *
 * The AppShell layout is rendered as a route layout element,
 * so all routes get the sidebar/topbar/mobile-nav shell.
 */
function App() {
  return (
    <BrowserRouter>
      <DataProvider>
        <Suspense fallback={<AppLoadingFallback />}>
          <FontGate fallback={<AppLoadingFallback />}>
            <ToastProvider>
              <AppRoutes />
              <CookieConsent />
              {import.meta.env.DEV && <DevSeeder />}
            </ToastProvider>
          </FontGate>
        </Suspense>
      </DataProvider>
    </BrowserRouter>
  );
}

function DevSeeder() {
  const repo = useRepository();
  
  useEffect(() => {
    if (import.meta.env.DEV) {
      void seedDevData(repo);
    }
  }, [repo]);
  
  return null;
}

export default App;
